/**
 * Razorpay Service
 * Handles payment gateway operations for biogas transactions
 */

declare global {
  interface Window {
    Razorpay: any;
  }
}

const isDevelopment = import.meta.env.DEV;
const baseUrl = isDevelopment
  ? '/biogas-service'
  : (import.meta.env.VITE_BIOGAS_SERVICE_URL || 'http://localhost:8082/biogas-service');
const RAZORPAY_API_URL = `${baseUrl}/api/v1/payments/razorpay`;

// Helper function to get JWT token
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('saubhagya_jwt_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// API Response type
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string | null;
}

// Razorpay Order Request
export interface RazorpayOrderRequest {
  transactionId: number;
  amount: number;
  gaushalaName: string;
  gaushalaId?: number;
  description?: string;
}

// Razorpay Order Response
export interface RazorpayOrderResponse {
  orderId: string;
  currency: string;
  amount: number;
  receipt: string;
  status: string;
  createdAt: number;
  transactionId: string;
  gaushalaName: string;
  razorpayKeyId: string;
}

// Razorpay Payment Verification Request
export interface RazorpayPaymentVerificationRequest {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
  transactionId: number;
}

// Razorpay Payment Response
export interface RazorpayPaymentResponse {
  paymentId: string;
  orderId: string;
  status: string;
  amount: number;
  currency: string;
  method: string;
  email?: string;
  contact?: string;
  description?: string;
  createdAt: number;
  captured: boolean;
  errorCode?: string;
  errorDescription?: string;
}

// Razorpay Refund Request
export interface RazorpayRefundRequest {
  paymentId: string;
  amount: number;
  reason: string;
  transactionId?: number;
}

/**
 * Load Razorpay checkout script
 */
export const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Create Razorpay order
 */
export const createOrder = async (
  data: RazorpayOrderRequest
): Promise<ApiResponse<RazorpayOrderResponse>> => {
  try {
    const response = await fetch(`${RAZORPAY_API_URL}/create-order`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to create order');
    }

    return result;
  } catch (error) {
    console.error('Error creating Razorpay order:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create order',
    };
  }
};

/**
 * Verify payment
 */
export const verifyPayment = async (
  data: RazorpayPaymentVerificationRequest
): Promise<ApiResponse<RazorpayPaymentResponse>> => {
  try {
    const response = await fetch(`${RAZORPAY_API_URL}/verify-payment`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Payment verification failed');
    }

    return result;
  } catch (error) {
    console.error('Error verifying payment:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Payment verification failed',
    };
  }
};

/**
 * Get payment details
 */
export const getPaymentDetails = async (
  paymentId: string
): Promise<ApiResponse<RazorpayPaymentResponse>> => {
  try {
    const response = await fetch(`${RAZORPAY_API_URL}/${paymentId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to get payment details');
    }

    return result;
  } catch (error) {
    console.error('Error getting payment details:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get payment details',
    };
  }
};

/**
 * Create refund
 */
export const createRefund = async (
  data: RazorpayRefundRequest
): Promise<ApiResponse<string>> => {
  try {
    const response = await fetch(`${RAZORPAY_API_URL}/refund`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to create refund');
    }

    return result;
  } catch (error) {
    console.error('Error creating refund:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create refund',
    };
  }
};

/**
 * Display Razorpay checkout
 */
export interface RazorpayCheckoutOptions {
  orderId: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  keyId: string;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: Record<string, any>;
  theme?: {
    color?: string;
  };
}

export const displayRazorpayCheckout = (
  options: RazorpayCheckoutOptions,
  onSuccess: (response: any) => void,
  onFailure: (error: any) => void
): void => {
  if (!window.Razorpay) {
    onFailure(new Error('Razorpay SDK not loaded'));
    return;
  }

  const rzpOptions = {
    key: options.keyId,
    amount: Math.round(options.amount * 100), // Convert to paise
    currency: options.currency,
    name: options.name,
    description: options.description,
    order_id: options.orderId,
    prefill: options.prefill,
    notes: options.notes,
    theme: options.theme || {
      color: '#10B981', // Green color for biogas theme
    },
    // Enable all payment methods including international cards
    config: {
      display: {
        preferences: {
          show_default_blocks: true, // Show all available payment methods
        },
      },
    },
    handler: function (response: any) {
      onSuccess(response);
    },
    modal: {
      ondismiss: function () {
        onFailure(new Error('Payment cancelled by user'));
      },
    },
  };

  const rzp = new window.Razorpay(rzpOptions);
  rzp.on('payment.failed', function (response: any) {
    onFailure(response.error);
  });
  rzp.open();
};

// Export everything as default as well
export default {
  loadRazorpayScript,
  createOrder,
  verifyPayment,
  getPaymentDetails,
  createRefund,
  displayRazorpayCheckout,
};
